import { useState } from "react";
import { Scale, AlertTriangle, CheckCircle2, XCircle, Phone, Mail, Globe, Printer, ChevronRight } from "lucide-react";

/**
 * Lemon Law page — full Massachusetts Used Vehicle Warranty Law (M.G.L. c. 90 § 7N¼)
 * formatted for on-screen reading. Three languages (PT / EN / ES) toggle the
 * whole document so the salesperson can show the customer the law in their
 * preferred language during pickup.
 *
 * For the signed PDF version with electronic signature, the user opens the
 * LemonLawWarrantyModal from the delivered car card on the Esteira de Entrega.
 */

const CONTENT = {
  pt: {
    eyebrow: "Lei de Massachusetts — M.G.L. c. 90 § 7N¼",
    title: "Garantia Limitada de Veículo Usado",
    intro: "Lei estadual obrigatória que protege todo consumidor que compra um carro usado em Massachusetts. Este texto pode ser lido para o cliente no momento da entrega do veículo.",
    sections: [
      {
        kind: "info",
        title: "Quais veículos estão cobertos",
        bullets: [
          "Carros, vans ou caminhões usados comprados de um concessionário em Massachusetts para uso pessoal ou familiar.",
          "Em Massachusetts, um concessionário é definido como alguém que vende mais de 3 carros em 12 meses.",
          "O carro deve custar pelo menos US$ 700.",
          "O carro deve ter menos de 125.000 milhas no odômetro no momento da venda.",
          "Motocicletas e veículos comerciais NÃO são elegíveis.",
        ],
      },
      {
        kind: "term",
        title: "Prazo de Proteção (baseado na quilometragem da venda)",
        rows: [
          { miles: "Menos de 40.000 milhas", term: "90 dias OU 3.750 milhas (o que vier primeiro)", color: "green" },
          { miles: "40.000 – 79.999 milhas", term: "60 dias OU 2.500 milhas (o que vier primeiro)", color: "green" },
          { miles: "80.000 – 124.999 milhas", term: "30 dias OU 1.250 milhas (o que vier primeiro)", color: "green" },
          { miles: "125.000 milhas ou mais", term: "NÃO coberto pela garantia legal", color: "amber" },
        ],
      },
      {
        kind: "ok",
        title: "O que ESTÁ coberto",
        text: "Qualquer defeito que prejudique substancialmente o uso ou a segurança do veículo e apareça durante o Prazo de Proteção.",
        examples: [
          "Falha de motor",
          "Problemas de transmissão",
          "Defeitos graves de direção",
          "Defeitos graves de suspensão",
          "Falhas de segurança no sistema de freios",
          "Problemas elétricos que afetam operação/segurança",
        ],
      },
      {
        kind: "not_ok",
        title: "O que NÃO está coberto",
        text: "Para carros usados em Massachusetts, a lei cobre principalmente defeitos que afetam o uso ou a segurança do veículo. Ela NÃO cobre itens de desgaste normal, manutenção, problemas estéticos ou danos causados após a compra.",
        groups: [
          { title: "Peças/itens geralmente NÃO cobertos", bullets: [
            "Pneus", "Pastilhas e lonas de freio", "Palhetas do limpador", "Lâmpadas",
            "Baterias (a menos que façam parte de outro defeito coberto)", "Velas de ignição",
            "Filtros", "Fluidos e troca de óleo", "Correias e mangueiras de manutenção", "Revisões de rotina",
          ]},
          { title: "Defeitos estéticos", bullets: [
            "Riscos na pintura", "Amassados", "Ferrugem", "Danos na tapeçaria",
            "Danos em acabamento interno",
            "Vidros trincados (exceto se ligados a um defeito de segurança)",
            "Sistemas de áudio/rádio (exceto se cobertos especificamente)",
            "Itens de desgaste por idade/quilometragem",
          ]},
          { title: "Danos causados por", bullets: [
            "Acidentes", "Mau uso", "Negligência", "Falta de manutenção",
            "Modificações feitas pelo comprador",
          ]},
          { title: "Também NÃO cobertos", bullets: [
            "Problemas causados pelo comprador após a compra",
            "Incômodos menores que não prejudicam o uso ou a segurança",
            "Condições pré-existentes divulgadas antes da venda",
          ]},
        ],
      },
      {
        kind: "info",
        title: "Como solicitar o reparo",
        bullets: [
          "Notifique o concessionário por telefone OU por escrito assim que o defeito aparecer.",
          "O concessionário deve aceitar o veículo para reparo em até 3 dias úteis.",
          "Franquia máxima por reparo: US$ 100 no total.",
          "O concessionário não pode negar o reparo, mas pode designar outra oficina para fazê-lo em seu nome.",
          "Guarde TODAS as ordens de serviço, recibos, e-mails e comunicações por escrito.",
        ],
      },
      {
        kind: "warn",
        title: "Direito de devolver o veículo",
        bullets: [
          "Após TRÊS tentativas de reparo do mesmo defeito sem solução, o comprador pode devolver o veículo para reembolso.",
          "Se o veículo ficar sem uso por um total acumulado de 11 DIAS ÚTEIS por qualquer combinação de defeitos, e o problema persistir, o comprador pode devolver para reembolso.",
          "O concessionário também pode oferecer POR ESCRITO recomprar o veículo em vez de tentar mais reparos.",
          "O comprador tem pelo menos 5 dias úteis para aceitar ou rejeitar uma oferta de recompra escrita.",
          "Recusar uma oferta de reembolso total encerra o direito a reparos adicionais em garantia.",
        ],
      },
      {
        kind: "info",
        title: "Cálculo do valor da recompra",
        text: "O valor da recompra inclui o preço total do contrato (com troca, taxas de documento), encargos de financiamento, taxas de registro, custo proporcional do seguro, parcela não reembolsável de garantias estendidas, reboque até 30 milhas, até US$ 15/dia em transporte alternativo (a partir do 3º dia fora de serviço), todos os pagamentos de reparo e franquias pagas. Subtrai-se: 15¢ por milha rodada da entrega até o reembolso, e quaisquer descontos do contrato.",
      },
      {
        kind: "warn",
        title: "Arbitragem (caso o concessionário se recuse)",
        text: "Se o concessionário recusar um reembolso devido, o comprador pode solicitar arbitragem estatal pelo OCABR (Office of Consumer Affairs and Business Regulation). Prazo: até 6 meses da data da venda. Sem taxa de inscrição. Taxa de árbitro de US$ 300 se aplica quando a audiência é agendada — incluída no prêmio se o comprador vencer. Resultado: refund ou substituição em ~70% dos casos válidos.",
      },
    ],
    contacts_title: "Contatos oficiais (para o cliente)",
    contacts: [
      { icon: "phone", label: "OCABR (Office of Consumer Affairs)", value: "(617) 973-8787" },
      { icon: "phone", label: "Attorney General — Consumer Division", value: "(617) 727-8400" },
      { icon: "web", label: "Site oficial", value: "www.mass.gov/lemon-law-arbitration" },
    ],
    footer: "Esta garantia NÃO PODE ser dispensada. O concessionário é obrigado por lei a entregá-la por escrito ao comprador no momento da venda.",
  },

  en: {
    eyebrow: "Massachusetts Law — M.G.L. c. 90 § 7N¼",
    title: "Limited Used Vehicle Warranty",
    intro: "Massachusetts state law protecting every consumer who buys a used car. This text can be read out loud to the customer at the moment of delivery.",
    sections: [
      {
        kind: "info",
        title: "Which vehicles are covered",
        bullets: [
          "Used cars, vans or trucks bought from a Massachusetts dealer for personal or family use.",
          "In Massachusetts, a dealer is defined as someone who sells more than 3 cars in 12 months.",
          "The car must cost at least $700.",
          "The car must have less than 125,000 miles on the odometer at the time of sale.",
          "Motorcycles and commercial vehicles are NOT eligible.",
        ],
      },
      {
        kind: "term",
        title: "Term of Protection (based on mileage at sale)",
        rows: [
          { miles: "Less than 40,000 miles", term: "90 days OR 3,750 miles (whichever first)", color: "green" },
          { miles: "40,000 – 79,999 miles", term: "60 days OR 2,500 miles (whichever first)", color: "green" },
          { miles: "80,000 – 124,999 miles", term: "30 days OR 1,250 miles (whichever first)", color: "green" },
          { miles: "125,000 miles or more", term: "NOT covered by the statutory warranty", color: "amber" },
        ],
      },
      {
        kind: "ok",
        title: "What IS covered",
        text: "Any defect that substantially impairs the use or safety of the vehicle and appears during the Term of Protection.",
        examples: [
          "Engine failure",
          "Transmission problems",
          "Major steering defects",
          "Serious suspension defects",
          "Brake system safety failures",
          "Electrical issues affecting operation/safety",
        ],
      },
      {
        kind: "not_ok",
        title: "What is NOT covered",
        text: "For used cars in Massachusetts, the law mainly covers defects that affect the vehicle's use or safety. It does NOT cover normal wear items, maintenance items, cosmetic issues, or damage caused after purchase.",
        groups: [
          { title: "Parts/items usually NOT covered", bullets: [
            "Tires", "Brake pads and brake linings", "Windshield wiper blades", "Light bulbs",
            "Batteries (unless part of another covered defect)", "Spark plugs",
            "Filters", "Fluids and oil changes", "Belts and hoses considered normal maintenance", "Routine tune-ups",
          ]},
          { title: "Cosmetic defects", bullets: [
            "Paint scratches", "Dents", "Rust", "Upholstery damage", "Interior trim damage",
            "Glass damage/cracks unless tied to a safety defect",
            "Audio systems / radios unless specifically warranted",
            "Wear-and-tear items due to age/mileage",
          ]},
          { title: "Damage caused by", bullets: [
            "Accidents", "Misuse", "Neglect", "Lack of maintenance",
            "Modifications made by the buyer",
          ]},
          { title: "Also generally NOT covered", bullets: [
            "Problems the buyer caused after purchase",
            "Minor annoyances that do not impair safety or use",
            "Pre-existing conditions disclosed before the sale",
          ]},
        ],
      },
      {
        kind: "info",
        title: "How to request a repair",
        bullets: [
          "Notify the dealer by phone OR in writing as soon as the defect appears.",
          "The dealer must accept the vehicle for repair within 3 business days.",
          "Maximum deductible per repair: $100 total.",
          "The dealer cannot deny the repair, but may arrange another shop to do the work on its behalf.",
          "Keep ALL repair orders, receipts, e-mails and written communications.",
        ],
      },
      {
        kind: "warn",
        title: "Right to return the vehicle",
        bullets: [
          "After THREE repair attempts for the same defect with no resolution, the buyer may return the vehicle for a refund.",
          "If the vehicle is out of service for a cumulative 11 BUSINESS DAYS for any combination of defects and a problem still exists, the buyer may return for a refund.",
          "The dealer may also offer IN WRITING to buy back the vehicle instead of attempting further repairs.",
          "The buyer has at least 5 business days to accept or reject a written buy-back offer.",
          "Refusing a full-refund offer terminates the right to further warranty repairs.",
        ],
      },
      {
        kind: "info",
        title: "Calculating the repurchase amount",
        text: "The repurchase amount includes total contract price (with trade-in, doc fees), finance charges, registration fees, pro-rata cost of insurance, non-refundable portion of extended warranties, towing up to 30 miles, up to $15/day in alternative transportation (starting day 3 out of service), all repair payments and deductibles paid. Subtract: 15¢ per mile driven from delivery to refund, and any rebates from the contract.",
      },
      {
        kind: "warn",
        title: "Arbitration (if the dealer refuses)",
        text: "If the dealer refuses a refund owed, the buyer may request state arbitration through OCABR (Office of Consumer Affairs and Business Regulation). Deadline: 6 months from the date of sale. No application fee. $300 arbitrator fee applies when a hearing is scheduled — included in the award if the buyer wins. Outcome: refund or replacement in ~70% of valid cases.",
      },
    ],
    contacts_title: "Official contacts (for the customer)",
    contacts: [
      { icon: "phone", label: "OCABR (Office of Consumer Affairs)", value: "(617) 973-8787" },
      { icon: "phone", label: "Attorney General — Consumer Division", value: "(617) 727-8400" },
      { icon: "web", label: "Official website", value: "www.mass.gov/lemon-law-arbitration" },
    ],
    footer: "This warranty CANNOT be waived. The dealer is required by law to provide it in writing to the buyer at the time of sale.",
  },

  es: {
    eyebrow: "Ley de Massachusetts — M.G.L. c. 90 § 7N¼",
    title: "Garantía Limitada de Vehículo Usado",
    intro: "Ley estatal de Massachusetts que protege a todo consumidor que compra un coche usado. Este texto puede leerse al cliente en el momento de la entrega del vehículo.",
    sections: [
      {
        kind: "info",
        title: "Qué vehículos están cubiertos",
        bullets: [
          "Coches, vans o camiones usados comprados a un concesionario de Massachusetts para uso personal o familiar.",
          "En Massachusetts, un concesionario se define como alguien que vende más de 3 coches en 12 meses.",
          "El coche debe costar al menos US$ 700.",
          "El coche debe tener menos de 125.000 millas en el odómetro al momento de la venta.",
          "Las motocicletas y vehículos comerciales NO son elegibles.",
        ],
      },
      {
        kind: "term",
        title: "Período de Protección (según el kilometraje en la venta)",
        rows: [
          { miles: "Menos de 40.000 millas", term: "90 días O 3.750 millas (lo que ocurra primero)", color: "green" },
          { miles: "40.000 – 79.999 millas", term: "60 días O 2.500 millas (lo que ocurra primero)", color: "green" },
          { miles: "80.000 – 124.999 millas", term: "30 días O 1.250 millas (lo que ocurra primero)", color: "green" },
          { miles: "125.000 millas o más", term: "NO cubierto por la garantía legal", color: "amber" },
        ],
      },
      {
        kind: "ok",
        title: "Qué SÍ está cubierto",
        text: "Cualquier defecto que perjudique sustancialmente el uso o la seguridad del vehículo y aparezca durante el Período de Protección.",
        examples: [
          "Falla del motor",
          "Problemas de transmisión",
          "Defectos graves de dirección",
          "Defectos graves de suspensión",
          "Fallas de seguridad en el sistema de frenos",
          "Problemas eléctricos que afectan operación/seguridad",
        ],
      },
      {
        kind: "not_ok",
        title: "Qué NO está cubierto",
        text: "Para coches usados en Massachusetts, la ley cubre principalmente defectos que afectan el uso o la seguridad del vehículo. NO cubre artículos de desgaste normal, mantenimiento, problemas cosméticos o daños causados después de la compra.",
        groups: [
          { title: "Piezas/artículos generalmente NO cubiertos", bullets: [
            "Llantas", "Pastillas y zapatas de freno", "Limpiaparabrisas", "Bombillas",
            "Baterías (a menos que sean parte de otro defecto cubierto)", "Bujías",
            "Filtros", "Fluidos y cambios de aceite", "Correas y mangueras de mantenimiento", "Afinaciones de rutina",
          ]},
          { title: "Defectos cosméticos", bullets: [
            "Rayones en la pintura", "Abolladuras", "Óxido", "Daños en tapicería",
            "Daños en acabados interiores",
            "Cristales rotos (excepto si se relacionan con un defecto de seguridad)",
            "Sistemas de audio/radio (excepto si están específicamente garantizados)",
            "Artículos de desgaste por edad/kilometraje",
          ]},
          { title: "Daños causados por", bullets: [
            "Accidentes", "Mal uso", "Negligencia", "Falta de mantenimiento",
            "Modificaciones hechas por el comprador",
          ]},
          { title: "Tampoco cubiertos", bullets: [
            "Problemas causados por el comprador después de la compra",
            "Molestias menores que no afectan el uso o la seguridad",
            "Condiciones preexistentes divulgadas antes de la venta",
          ]},
        ],
      },
      {
        kind: "info",
        title: "Cómo solicitar la reparación",
        bullets: [
          "Notifique al concesionario por teléfono O por escrito tan pronto aparezca el defecto.",
          "El concesionario debe aceptar el vehículo para reparación dentro de 3 días hábiles.",
          "Deducible máximo por reparación: US$ 100 en total.",
          "El concesionario no puede negar la reparación, pero puede asignar otro taller para hacerla en su nombre.",
          "Guarde TODAS las órdenes de reparación, recibos, correos electrónicos y comunicaciones escritas.",
        ],
      },
      {
        kind: "warn",
        title: "Derecho a devolver el vehículo",
        bullets: [
          "Después de TRES intentos de reparación del mismo defecto sin solución, el comprador puede devolver el vehículo para reembolso.",
          "Si el vehículo está fuera de servicio por un total acumulado de 11 DÍAS HÁBILES por cualquier combinación de defectos y el problema persiste, puede devolverlo para reembolso.",
          "El concesionario también puede ofrecer POR ESCRITO recomprar el vehículo en lugar de intentar más reparaciones.",
          "El comprador tiene al menos 5 días hábiles para aceptar o rechazar una oferta escrita de recompra.",
          "Rechazar una oferta de reembolso completo termina el derecho a reparaciones adicionales bajo garantía.",
        ],
      },
      {
        kind: "info",
        title: "Cálculo del monto de recompra",
        text: "El monto incluye precio total del contrato (con intercambio, cuotas de documento), cargos de financiamiento, cuotas de registro, costo proporcional del seguro, porción no reembolsable de garantías extendidas, grúa hasta 30 millas, hasta US$ 15/día en transporte alternativo (desde el día 3 fuera de servicio), todos los pagos de reparación y deducibles pagados. Se sustrae: 15¢ por milla recorrida desde la entrega hasta el reembolso, y cualquier descuento del contrato.",
      },
      {
        kind: "warn",
        title: "Arbitraje (si el concesionario se niega)",
        text: "Si el concesionario rechaza un reembolso debido, el comprador puede solicitar arbitraje estatal a través de OCABR (Office of Consumer Affairs and Business Regulation). Plazo: 6 meses desde la fecha de venta. Sin cuota de solicitud. Cuota de árbitro de US$ 300 cuando se programa una audiencia — incluida en la adjudicación si el comprador gana. Resultado: reembolso o reemplazo en ~70% de los casos válidos.",
      },
    ],
    contacts_title: "Contactos oficiales (para el cliente)",
    contacts: [
      { icon: "phone", label: "OCABR (Office of Consumer Affairs)", value: "(617) 973-8787" },
      { icon: "phone", label: "Attorney General — Consumer Division", value: "(617) 727-8400" },
      { icon: "web", label: "Sitio oficial", value: "www.mass.gov/lemon-law-arbitration" },
    ],
    footer: "Esta garantía NO PUEDE ser renunciada. El concesionario está obligado por ley a entregarla por escrito al comprador en el momento de la venta.",
  },
};

const SECTION_STYLES = {
  info: { border: "border-border", icon: ChevronRight, iconColor: "text-text-secondary", titleColor: "text-text-primary" },
  ok: { border: "border-success/40", icon: CheckCircle2, iconColor: "text-success", titleColor: "text-success" },
  not_ok: { border: "border-primary/40", icon: XCircle, iconColor: "text-primary", titleColor: "text-primary" },
  warn: { border: "border-warning/40", icon: AlertTriangle, iconColor: "text-warning", titleColor: "text-warning" },
  term: { border: "border-success/40", icon: Scale, iconColor: "text-success", titleColor: "text-text-primary" },
};

export default function LemonLawPage() {
  const [lang, setLang] = useState(() => localStorage.getItem("lemon_law_lang") || "pt");
  const setLangSticky = (v) => { setLang(v); localStorage.setItem("lemon_law_lang", v); };
  const c = CONTENT[lang];

  return (
    <div data-testid="lemon-law-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 mb-2">
            <Scale size={18} className="text-primary" />
            <p className="label-eyebrow text-primary">{c.eyebrow}</p>
          </div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">
            {c.title}
          </h1>
          <p className="text-text-secondary max-w-3xl mt-3 text-sm leading-relaxed">{c.intro}</p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="lemon-law-print"
          className="border border-border hover:border-primary hover:text-primary transition-colors px-4 h-11 inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest"
        >
          <Printer size={14} /> Imprimir
        </button>
      </div>

      {/* Language tabs */}
      <div className="inline-flex border border-border bg-surface p-0.5">
        {[
          { code: "pt", label: "Português", flag: "PT" },
          { code: "en", label: "English", flag: "EN" },
          { code: "es", label: "Español", flag: "ES" },
        ].map(opt => (
          <button
            key={opt.code}
            type="button"
            data-testid={`lemon-law-lang-${opt.code}`}
            onClick={() => setLangSticky(opt.code)}
            className={`px-5 h-10 text-xs font-display font-bold uppercase tracking-widest transition-colors ${
              lang === opt.code ? "bg-primary text-white" : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <span className="mr-1.5 opacity-60">{opt.flag}</span>{opt.label}
          </button>
        ))}
      </div>

      {/* Sections */}
      <div className="space-y-5 max-w-5xl">
        {c.sections.map((sec, idx) => {
          const S = SECTION_STYLES[sec.kind] || SECTION_STYLES.info;
          const Icon = S.icon;
          return (
            <div
              key={idx}
              data-testid={`lemon-law-section-${idx}`}
              className={`border ${S.border} bg-background p-6`}
            >
              <div className="flex items-center gap-3 mb-4">
                <Icon size={20} className={S.iconColor} />
                <h2 className={`font-display font-black text-lg uppercase tracking-tight ${S.titleColor}`}>
                  {sec.title}
                </h2>
              </div>

              {sec.text && (
                <p className="text-text-primary text-base leading-relaxed mb-3">{sec.text}</p>
              )}

              {sec.examples && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {sec.examples.map((ex, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 border border-border bg-surface px-3 py-1.5 text-xs">
                      <CheckCircle2 size={11} className="text-success" /> {ex}
                    </span>
                  ))}
                </div>
              )}

              {sec.bullets && (
                <ul className="space-y-2">
                  {sec.bullets.map((b, i) => (
                    <li key={i} className="flex gap-3 text-base text-text-primary leading-relaxed">
                      <ChevronRight size={16} className={`${S.iconColor} mt-1 shrink-0`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}

              {sec.groups && (
                <div className="space-y-5 mt-3">
                  {sec.groups.map((g, gi) => (
                    <div key={gi}>
                      <h3 className={`font-display font-bold text-sm uppercase tracking-wider mb-2 ${S.titleColor}`}>
                        {g.title}
                      </h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                        {g.bullets.map((b, bi) => (
                          <li key={bi} className="flex gap-2 text-sm text-text-primary leading-relaxed">
                            <span className={`${S.iconColor} mt-0.5 shrink-0`}>•</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {sec.rows && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {sec.rows.map((r, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="py-3 pr-4 font-display font-bold">{r.miles}</td>
                          <td className={`py-3 ${r.color === "green" ? "text-success" : "text-warning"} font-display font-bold`}>
                            {r.term}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Contacts */}
      <div className="max-w-5xl border border-primary/40 bg-primary/5 p-6">
        <h3 className="font-display font-black text-lg uppercase tracking-tight mb-4">
          {c.contacts_title}
        </h3>
        <ul className="space-y-3">
          {c.contacts.map((ct, i) => {
            const Ico = ct.icon === "phone" ? Phone : ct.icon === "web" ? Globe : Mail;
            return (
              <li key={i} className="flex items-center gap-4 text-base">
                <div className="w-9 h-9 rounded-full border border-primary/40 bg-primary/10 flex items-center justify-center shrink-0">
                  <Ico size={14} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-secondary uppercase tracking-widest font-display font-bold">{ct.label}</p>
                  <p className="font-display font-bold text-text-primary">{ct.value}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Footer */}
      <div className="max-w-5xl border-l-4 border-warning bg-warning/5 px-5 py-4">
        <p className="text-sm text-warning/90 leading-relaxed">
          <AlertTriangle size={14} className="inline mr-2 -mt-1" />
          {c.footer}
        </p>
      </div>
    </div>
  );
}
